import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import "../styles/Register.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getApiBaseUrl } from "../utils/baseUrl";
import RegistrationStructure from "../components/RegistrationStructure";
import PasswordStrength from "../components/PasswordStrength";
import { getPasswordStrength, passwordPolicyMessage } from "../utils/passwordStrength";

const BASE_URL = getApiBaseUrl();

const Register = () => {
  const navigate = useNavigate();
  const registerStyle = {
    "--auth-logo": `url(${process.env.PUBLIC_URL}/army-logo.png)`,
  };

  const [unit, setUnit] = useState(""); // Service code sent to the backend as `unit`
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [name, setName] = useState("");
  const [serviceNumber, setServiceNumber] = useState("");
  const [rank, setRank] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [signatureFile, setSignatureFile] = useState(null);
  const [stampFile, setStampFile] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const resetForm = () => {
    setUnit(""); setSelectedAppointment(null);
    setName(""); setServiceNumber(""); setRank(""); setEmail(""); setPhone("");
    setPassword(""); setSignatureFile(null); setStampFile(null);
    setFieldErrors({});
  };

  const setError = (field, msg) =>
    setFieldErrors((prev) => ({ ...prev, [field]: msg }));

  const clearFieldError = (field) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const validateForm = () => {
    const errors = {};

    if (!unit) errors.unit = "Select a service.";
    if (!selectedAppointment || selectedAppointment.unit !== unit) errors.role = "Select an appointment.";

    if (!name.trim()) errors.name = "Full name is required.";
    if (!serviceNumber.trim()) errors.serviceNumber = "Service number is required.";
    if (!email.trim()) errors.email = "Email is required.";
    if (!phone.trim()) errors.phone = "Phone is required.";
    if (!rank) errors.rank = "Select a rank.";
    if (!password) errors.password = "Password is required.";
    else if (!getPasswordStrength(password).valid) errors.password = passwordPolicyMessage;
    if (!signatureFile) errors.signature = "Signature image is required.";
    if (!stampFile) errors.stamp = "Stamp image is required.";

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fix the highlighted errors and try again.");
      return;
    }
    setLoading(true);

    const formData = new FormData();
    formData.append("unit", unit);
    formData.append("name", name);
    formData.append("serviceNumber", serviceNumber);
    formData.append("rank", rank);
    for (const key of ["role", "branch", "subBranch", "unitBranch"]) {
      formData.append(key, selectedAppointment[key] || "");
    }
    formData.append("appointmentId", selectedAppointment.id);
    formData.append("password", password);
    formData.append("email", email);
    formData.append("phone", phone);
    if (signatureFile) formData.append("signature", signatureFile);
    if (stampFile) formData.append("stamp", stampFile);

    try {
      const res = await fetch(`${BASE_URL}/api/users/register`, { method: "POST", body: formData });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const message = data.message || "Registration failed.";
        toast.error(message);
        if (data.field) setError(data.field, message);
        return;
      }
      toast.success(data.message || `Registered under ${unit}!`);
      resetForm();
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      toast.error(err.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
   
      <ToastContainer />

      <div className="register-page" style={registerStyle}>
        <div className="register-container">
          <span className="register-kicker">HQ Kenya Army</span>
          <h2 className="mb-8 text-3xl font-bold text-center register-title">USER REGISTRATION</h2>
          <p className="register-subtitle">
            Create an account aligned to your service, formation, and appointment.
          </p>

          <form onSubmit={handleRegister} className="space-y-6 register-form">

            {/* 1. SELECT SERVICE */}
            <div className={`p-6 bg-blue-100 border-4 border-blue-700 rounded-lg ${fieldErrors.unit ? "section-error" : ""}`}>
              <label className="text-xl font-bold text-blue-900">1. Select Your Service</label>
                <select
                  value={unit}
                  onChange={(e) => {
                    setUnit(e.target.value);
                    setSelectedAppointment(null);
                    setFieldErrors({});
                    clearFieldError("unit");
                  }}
                  required
                  className={`w-full p-4 mt-3 text-lg font-bold border-2 border-blue-800 rounded ${fieldErrors.unit ? "field-error-input" : ""}`}
                >
                <option value="">Choose Service</option>
                <option value="KA">KA</option>
                <option value="KAF">KAF</option>
                <option value="KN">KN</option>
              </select>
              {fieldErrors.unit && <p className="field-error">{fieldErrors.unit}</p>}
            </div>

            {unit && <RegistrationStructure key={unit} service={unit} onChange={setSelectedAppointment} />}
            {fieldErrors.role && <p className="field-error">{fieldErrors.role}</p>}

            {/* PERSONAL DETAILS */}
            {unit && (
              <>
                <input type="text" placeholder="Full Name" value={name} onChange={(e) => { setName(e.target.value); clearFieldError("name"); }} required className={`w-full p-4 border rounded ${fieldErrors.name ? "field-error-input" : ""}`} />
                {fieldErrors.name && <p className="field-error">{fieldErrors.name}</p>}
                <input type="text" placeholder="Service Number" value={serviceNumber} onChange={(e) => { setServiceNumber(e.target.value); clearFieldError("serviceNumber"); }} required className={`w-full p-4 border rounded ${fieldErrors.serviceNumber ? "field-error-input" : ""}`} />
                {fieldErrors.serviceNumber && <p className="field-error">{fieldErrors.serviceNumber}</p>}
                <input type="email" placeholder="Email" value={email} onChange={(e) => { setEmail(e.target.value); clearFieldError("email"); }} required className={`w-full p-4 border rounded ${fieldErrors.email ? "field-error-input" : ""}`} />
                {fieldErrors.email && <p className="field-error">{fieldErrors.email}</p>}
                <input type="tel" placeholder="Phone" value={phone} onChange={(e) => { setPhone(e.target.value); clearFieldError("phone"); }} required className={`w-full p-4 border rounded ${fieldErrors.phone ? "field-error-input" : ""}`} />
                {fieldErrors.phone && <p className="field-error">{fieldErrors.phone}</p>}

                <select value={rank} onChange={(e) => { setRank(e.target.value); clearFieldError("rank"); }} required className={`w-full p-4 border rounded ${fieldErrors.rank ? "field-error-input" : ""}`}>
                  <option value="">Select Rank</option>
                  <optgroup label="Officers">
                    <option>General</option><option>Lieutenant General</option><option>Major General</option>
                    <option>Brigadier</option><option>Colonel</option><option>Lieutenant Colonel</option>
                    <option>Major</option><option>Captain</option><option>Lieutenant</option><option>Second Lieutenant</option>
                  </optgroup>
                  <optgroup label="NCOs & Enlisted">
                    <option>Warrant Officer I</option><option>Warrant Officer II</option>
                    <option>Senior Sergeant</option><option>Sergeant</option><option>Corporal</option>
                    <option>Lance Corporal</option><option>Private</option>
                  </optgroup>
                </select>
                {fieldErrors.rank && <p className="field-error">{fieldErrors.rank}</p>}

                <div className="relative">
                  <input type={showPassword ? "text" : "password"} placeholder="Password" value={password} onChange={(e) => { setPassword(e.target.value); clearFieldError("password"); }} required className={`w-full p-4 border rounded ${fieldErrors.password ? "field-error-input" : ""}`} />
                  <span onClick={() => setShowPassword(!showPassword)} className="absolute cursor-pointer right-4 top-5">Show</span>
                </div>
                <PasswordStrength password={password} />
                {fieldErrors.password && <p className="field-error">{fieldErrors.password}</p>}

                <label className="block mt-2 text-sm font-semibold text-gray-700">Signature Image</label>
                <input type="file" accept="image/*" onChange={(e) => { setSignatureFile(e.target.files[0]); clearFieldError("signature"); }} required className={`w-full p-4 border rounded ${fieldErrors.signature ? "field-error-input" : ""}`} />
                {fieldErrors.signature && <p className="field-error">{fieldErrors.signature}</p>}
                <label className="block mt-2 text-sm font-semibold text-gray-700">Stamp Image</label>
                <input type="file" accept="image/*" onChange={(e) => { setStampFile(e.target.files[0]); clearFieldError("stamp"); }} required className={`w-full p-4 border rounded ${fieldErrors.stamp ? "field-error-input" : ""}`} />
                {fieldErrors.stamp && <p className="field-error">{fieldErrors.stamp}</p>}
              </>
            )}

            <button type="submit" disabled={loading || !unit || !selectedAppointment} className="w-full py-5 text-xl font-bold text-white bg-green-700 rounded hover:bg-green-800">
              {loading ? "REGISTERING..." : "REGISTER USER"}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default Register;
